/**
 * Generic ErrorBoundary
 *
 * Reusable error boundary for gracefully handling rendering failures.
 */

import { Component, ErrorInfo, ReactNode } from "react";
import { AlertTriangle, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  isChunkLoadError,
  isChunkReloadImminent,
  requestChunkReload,
} from "@/lib/chunk-reload";

interface Props {
  children: ReactNode;
  /** Optional fallback to render instead of default error UI */
  fallback?: ReactNode;
  /** Label shown in the error message (e.g. "rock details", "scorecard") */
  label?: string;
  /** Callback when an error is caught */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  // Escape hatch for the quiet "Updating…" state: if the auto-reload never
  // lands (blocked by a beforeunload "Stay" click, paused bfcache, …) the
  // spinner must not be a dead end — after this flips we fall through to the
  // warning UI with its hard-reload button.
  chunkReloadTimedOut: boolean;
}

// How long the quiet "Updating…" state waits for the in-flight reload before
// giving the user the manual warning/Reload UI instead.
const CHUNK_RELOAD_GRACE_MS = 6000;

export class ErrorBoundary extends Component<Props, State> {
  private chunkReloadTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, chunkReloadTimedOut: false };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error("ErrorBoundary caught:", error, errorInfo);
    // A chunk-load failure self-heals via the once-per-session reload (see
    // lib/chunk-reload). Usually main.tsx's vite:preloadError handler already
    // fired it; this covers import paths the preload helper doesn't wrap.
    if (isChunkLoadError(error) && requestChunkReload()) {
      // Normally the reload replaces the page before this fires. If it's
      // still mounted after the grace window, the reload was blocked — stop
      // spinning and surface the manual path.
      this.chunkReloadTimer = setTimeout(
        () => this.setState({ chunkReloadTimedOut: true }),
        CHUNK_RELOAD_GRACE_MS,
      );
    }
    this.props.onError?.(error, errorInfo);
  }

  componentWillUnmount(): void {
    if (this.chunkReloadTimer !== null) {
      clearTimeout(this.chunkReloadTimer);
    }
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null, chunkReloadTimedOut: false });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      const chunkError = isChunkLoadError(this.state.error);
      // Stale-chunk failure with the auto-reload in flight (or about to fire
      // from componentDidCatch): the page is seconds from replacing itself
      // with the freshly deployed version. Render a quiet updating state —
      // flashing the warning page here is the jarring part, not the error.
      // ``chunkReloadTimedOut`` is the escape hatch: a blocked reload drops
      // through to the warning UI below instead of spinning forever.
      if (
        chunkError &&
        isChunkReloadImminent() &&
        !this.state.chunkReloadTimedOut
      ) {
        return (
          <div
            className="flex flex-col items-center justify-center gap-3 px-4 py-16 text-center"
            role="status"
            aria-live="polite"
          >
            <Loader2
              className="h-6 w-6 animate-spin text-muted-foreground motion-reduce:animate-none"
              aria-hidden
            />
            <p className="text-sm text-muted-foreground">
              Updating to the latest version…
            </p>
          </div>
        );
      }

      if (this.props.fallback) {
        return this.props.fallback;
      }

      const label = this.props.label ?? "content";

      return (
        <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
          <AlertTriangle className="h-10 w-10 text-amber-500 mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">
            Unable to load {label}
          </h3>
          <p className="text-sm text-muted-foreground mb-4 max-w-md">
            {chunkError
              ? "This page's code couldn't be loaded — your session may be on an outdated version."
              : "There was an error rendering this section. This may be due to unexpected data or a temporary issue."}
          </p>
          <Button
            variant="outline"
            size="sm"
            // A chunk give-up means the once-per-session reload already
            // happened and the import still fails — a state reset would just
            // re-throw. Only a hard reload can fetch fresh code.
            onClick={
              chunkError ? () => window.location.reload() : this.handleRetry
            }
            className="gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            {chunkError ? "Reload" : "Try Again"}
          </Button>
          {import.meta.env.DEV && this.state.error && (
            <details className="mt-4 text-left text-xs text-muted-foreground max-w-lg">
              <summary className="cursor-pointer hover:text-foreground">
                Error details
              </summary>
              <pre className="mt-2 p-2 bg-muted rounded text-[10px] overflow-auto max-h-32">
                {this.state.error.message}
                {"\n\n"}
                {this.state.error.stack}
              </pre>
            </details>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}
